import _ from "lodash";
import moment from "moment";
import { ColorService } from "../ColorService/ColorService";

export class ChartStructure {
    constructor({ FormatUnits, ThemesService } = {}) {
        this.FormatUnits = FormatUnits;
        this.ThemesService = ThemesService;

        this.ColorService = new ColorService({
            ThemesService: this.ThemesService
        });
    }

    formatSeries({
        series = [],
        points = [],
        category = "",
        structureSeries = []
    } = {}) {
        if (!points.length) return series;
        const pointsGrouped = _.groupBy(points, "s");

        const seriesFormatted = series.map((serie, index) => {
            const seriePoints = pointsGrouped[serie._id];

            const structureSerie = structureSeries.find(
                (s) => s._id === serie._id
            );

            let color = "";

            if (structureSerie)
                color = this.ColorService.colorByPath(structureSerie.color);
            else color = serie.color;

            if (category === "pie")
                return {
                    ...serie,
                    ...this.formatPointsData(seriePoints, category, serie.path),
                    color
                };

            return {
                ...serie,
                data: this.formatPointsData(seriePoints, category, serie.path),
                color
            };
        });

        if (category === "pie") return [{ data: seriesFormatted }];

        return seriesFormatted;
    }

    formatTooltip() {
        const _formatValue = this.FormatUnits.formatValue;

        return {
            formatter: function () {
                const unit =
                    _.get(this.series, "userOptions.unit") ||
                    _.get(this.point, "unit");

                const units = {
                    currency: 2,
                    number: 0,
                    percentage: 1,
                    int: 0
                };

                const serieName =
                    this.point.type === "pie" ? this.key : this.series.name;

                const props = {
                    serie: _formatValue(
                        "percentage",
                        _.get(this.point, "metaProps.total", 0)
                    ),
                    high: _formatValue(
                        "int",
                        _.get(this.point, "metaProps.high", 0)
                    ),
                    medium: _formatValue(
                        "int",
                        _.get(this.point, "metaProps.medium", 0)
                    ),
                    low: _formatValue(
                        "int",
                        _.get(this.point, "metaProps.low", 0)
                    )
                };

                if (
                    _.get(this.series, "userOptions._id") === "SCORE_ACCOUNTING"
                ) {
                    return `
                        ${serieName}: ${props.serie}
                        <br>
                        Alto: ${props.high}
                        <br>
                        Médio: ${props.medium}
                        <br>
                        Baixo: ${props.low}
                    `;
                }

                return `${serieName} ${_formatValue(
                    unit,
                    this.y,
                    units[unit]
                )}`;
            }
        };
    }

    formatPointsData(points = [], category, path = "") {
        if (category === "pie") {
            const lastPoint = _.last(points) || { v: 0, t: new Date() };

            return { y: lastPoint.v, x: lastPoint.t.getTime() };
        }

        return points.map((point) => {
            const pointY =
                typeof point.v === "object"
                    ? _.get(point, `v.${path}`, 0)
                    : point.v;

            const pointFormatted = {
                y: pointY,
                x: point.t.getTime(),
                metaProps: point.v
            };

            if (_.get(point, "v.color"))
                pointFormatted.color = this.ColorService.colorByPath(
                    point.v.color
                );

            return pointFormatted;
        });
    }

    formatYAxis({ series = [], structure = {} } = {}) {
        if (!structure.yAxis) return;
        const units = {
            currency: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                _.set(axis, "labels.formatter", this._currencyLabelFormatter());
                return axis;
            },
            percentage: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                _.set(axis, "labels.format", "{value:.0f}%");
                return axis;
            },
            number: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                _.set(axis, "labels.format", "{value}");
                return axis;
            },
            int: (serieYAxis) => {
                const axis = angular.copy(serieYAxis);

                _.set(axis, "labels.format", "{value}");
                return axis;
            }
        };

        if (typeof structure.yAxis === "object")
            return units[series[0].unit](structure.yAxis);

        const yAxis = [];
        series.forEach((serie, index) => {
            let serieYAxis = _.get(structure, `yAxis[${index}]`);

            const axis = units[serie.unit || "currency"](serieYAxis);
            yAxis.push(axis);
        });

        return yAxis;
    }

    formatXAxis({ points = [] }) {
        if (!points.length) return { labels: {}, type: "datetime" };

        const firstPoint = _.head(points);
        const lastPoint = _.last(points);

        const firstPointFormatted = moment(firstPoint.t)
            .utcOffset(0)
            .format("x");

        const lastPointFormatted = moment(lastPoint.t).utcOffset(0).format("x");

        const min = parseInt(firstPointFormatted);
        const max = parseInt(lastPointFormatted);

        return { labels: {}, max, min, type: "datetime", startOnTick: true };
    }

    format({ structure = {}, series = [], points = [] } = {}) {
        let _structure = angular.copy(structure);

        _structure.series = this.formatSeries({
            series,
            points,
            category: _structure.category,
            structureSeries: structure.series
        });
        _structure.tooltip = this.formatTooltip();
        _structure.yAxis = this.formatYAxis({
            series,
            structure: angular.copy(_structure)
        });
        _structure.xAxis = this.formatXAxis({ points });

        return _structure;
    }

    _currencyLabelFormatter() {
        return function () {
            function kFormatter(num) {
                if (Math.abs(num) < 1e6)
                    return (
                        Math.sign(num) * (Math.abs(num) / 1e3).toFixed(1) + "k"
                    );
                else if (Math.abs(num) >= 1e6)
                    return (
                        Math.sign(num) * (Math.abs(num) / 1e6).toFixed(1) + "M"
                    );
            }

            if (Math.abs(this.value) > 999)
                return "R$ " + kFormatter(this.value);
            return this.value.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
            });
        };
    }
}
